/**
 * Gateway client: device discovery and register reads for the "Connect a
 * device" wizard (routes/connect.tsx), relayed through the cloud.
 *
 * Transport: a hosted browser can never reach a gateway PC's loopback config
 * API, so both calls go to the platform API instead (`things.discover` /
 * `things.sniff`, the same authenticated, org-scoped client every other
 * `things.*` call uses). The API publishes the scan on the chosen gateway's own
 * mTLS command channel, the gateway runs it on its plant network, and the answer
 * comes back on its command ack. Either call can take up to about 25 s (the
 * API's wait, under its 30 s request limit), so neither is aborted early.
 *
 * Contract: the answers are the gateway's raw discovery shapes (discover
 * `hits[]`, plus a pre-v2 gateway's `found[]` hosts; sniff `cells[]`; browse
 * `entries[]`, snake_case; lib/api/things). Discover and sniff answers are
 * MAPPED here into the wizard's stable `DiscoveredDevice` / `SniffedRegister`
 * view types, so the UI panels never depend on the wire shape. A v2 gateway
 * answers discover with `hits` only; reading `found` alone listed nothing on
 * one (the 2026-09-27 P0), so both are read.
 *
 * No backend, no scan: in stub mode (`isLiveApi` false) there is no gateway to
 * ask, so both calls throw an explanation. Nothing here invents devices or
 * register values.
 *
 * Go-live is NOT here: the wizard commits through the cloud (commit.ts
 * `commitConnection` -> createInstrument -> the cloud's config_push over the
 * broker, which the agent applies + hot-reloads). The cloud is the single source
 * of truth for what the agent polls; this client only reads the LAN.
 */

import {
  isLiveApi,
  things,
  type BrowseRequestBody,
  type BrowseResult,
  type DiscoverHit,
  type ModbusWordOrder,
  type DiscoverScanResult,
  type ScannedHost,
  type SniffScanCell,
} from "@/lib/api";
import { errorCodeOf, errorDetailOf, statusOf } from "@/lib/api/error";
import { DRIVER, driverDefaultPort, driverLabel, isModbusDriver } from "@/lib/iot/drivers";

/** A device found on the plant network by the gateway's discovery sweep (or keyed in by hand). */
export interface DiscoveredDevice {
  ip: string;
  /** Modbus unit / slave id: the unit that answered (0 when a TCP-open host answered no Modbus unit, and for other drivers). */
  unit_id: number;
  /** TCP port the device was found on. Unset for a manually keyed device (the driver's default port is assumed). */
  port?: number;
  /** The field driver the device answered on (modbus-tcp, opcua, s7, enip, melsec, ...). */
  driver: string;
  /**
   * What the driver reported about the device: vendor, product, endpoint (OPC
   * UA), unit_id (Modbus), rack / slot (S7), sample_value, ... Empty for a
   * pre-v2 gateway's host and for a manual entry.
   */
  identity: Record<string, string>;
  /** The reported vendor, "" when none (Modbus has no standard vendor register, so none is guessed). */
  vendor: string;
  /** Whether the device actually answered its protocol probe (a pre-v2 gateway also lists hosts that only had the port open). */
  online: boolean;
  /** Optional friendly name (else derived from ip). */
  name?: string;
  /** The range the scan that found it swept ("" = the gateway's own network); unset for a manual entry. */
  subnet?: string;
}

/** A stable id for a discovered device: one IP can answer on several drivers and ports. */
export function deviceKey(d: Pick<DiscoveredDevice, "ip" | "port" | "driver">): string {
  return `${d.ip}:${d.port ?? ""}:${d.driver}`;
}

/**
 * A device keyed in by hand ("Add manually"). A serial driver has no address:
 * its connection (serial port, line settings) is set on the Protocol step.
 */
export function manualDevice(opts: {
  driver: string;
  host: string;
  port?: number;
  unitId?: number;
  serial?: boolean;
}): DiscoveredDevice {
  const host = opts.serial ? "" : opts.host.trim();
  const modbus = opts.driver === DRIVER.MODBUS_TCP || opts.driver === DRIVER.MODBUS_RTU;
  return {
    ip: host,
    port: opts.serial ? undefined : opts.port,
    driver: opts.driver,
    identity: {},
    unit_id: modbus ? (opts.unitId ?? 1) : 0,
    vendor: "",
    online: false,
    name: opts.serial ? `${driverLabel(opts.driver)} device` : defaultDeviceName(opts.driver, host),
  };
}

/** "192.168.1.40:502 · unit 1" (the unit only for Modbus, when one answered); "serial" for a serial device. */
export function deviceAddressLabel(d: DiscoveredDevice): string {
  if (!d.ip) return "serial";
  const port = d.port ?? driverDefaultPort(d.driver);
  const unit = isModbusDriver(d.driver) && d.unit_id > 0 ? ` · unit ${d.unit_id}` : "";
  return `${d.ip}${port ? `:${port}` : ""}${unit}`;
}

/** The name a discovered device is offered under (and its instrument is created as). */
export function defaultDeviceName(driver: string, ip: string): string {
  // Modbus keeps today's "PLC <ip>" so an old gateway's flow is unchanged; other
  // drivers are named by protocol, since one address can host several of them.
  return driver === DRIVER.MODBUS_TCP || driver === DRIVER.MODBUS_RTU ? `PLC ${ip}` : `${driverLabel(driver)} ${ip}`;
}

/** One register read during a device's register sweep. */
export interface SniffedRegister {
  /** Modbus register address (holding-register offset). */
  address: number;
  /** Decoded wire type. */
  data_type: "int16" | "int32" | "float32" | "double";
  /** Number of 16-bit registers this value spans (1 int16, 2 int32/float32). */
  reg_count: number;
  /** Sampled value at sniff time (numeric, or a short string for identity regs). */
  value: number | string;
  /** Metric name: a placeholder such as `hr_12` from a sniff, or a loaded map's name. */
  guessed_name: string;
  /** Engineering unit, when a loaded map carries one (a sniff never guesses one). */
  unit?: string;
  /** Register space, when not the holding registers a sniff reads (a v2 browse can list any space). */
  space?: "holding" | "input" | "coil" | "discrete";
  /**
   * Read as unsigned: uint16 (data_type int16) or uint32 (data_type int32). Set when the gateway
   * reported the word as uint16, or when the operator picks an unsigned type on the Points step.
   */
  unsigned?: boolean;
  /**
   * The raw 16-bit register content as read (a sniff or browse), kept so the Points step can
   * re-decode a row retyped to a 2- or 4-register value from the words already read.
   */
  word?: number;
  /** Register order of a value spanning 2+ registers; undefined => "big" (ABCD). */
  word_order?: ModbusWordOrder;
  /** Linear scale the gateway applies (real = raw x scale); undefined => 1. */
  scale?: number;
  /** A loaded map's poll interval for this point, whole seconds; undefined => the Map step's default. */
  poll_sec?: number;
  /** A loaded map's deadband (publish only when the value moves more than this). */
  deadband?: number;
  /** A loaded map's heartbeat: publish at least this often (seconds) even when the value holds. */
  max_silence_sec?: number;
}

export interface DiscoverResult {
  devices: DiscoveredDevice[];
  /** The sweep ran out of time: every listed device is real, but the list may be incomplete. */
  partial: boolean;
}

export interface SniffResult {
  ip: string;
  unit_id: number;
  registers: SniffedRegister[];
}

/** Short space -> tag-name prefix, so a sniffed register seeds a unique, honest
 *  placeholder metric the operator renames on the Map step (the agent does not
 *  guess names). */
const SPACE_PREFIX: Record<string, string> = {
  holding: "hr",
  input: "ir",
  coils: "coil",
  discretes: "di",
};

/** The default register read: holding registers 0..63, in Modbus reads of 16. */
const SNIFF_START = 0;
const SNIFF_COUNT = 64;
const SNIFF_BLOCK = 16;

// ── Wire -> view mappers ─────────────────────────────────────────────────────

/** A discovery hit -> the wizard's device row. Every returned host had TCP open;
 *  `online` reflects whether a Modbus slave actually answered. */
function toDevice(f: ScannedHost): DiscoveredDevice {
  return {
    ip: f.host,
    unit_id: f.unit_id,
    port: f.port,
    driver: DRIVER.MODBUS_TCP,
    identity: {},
    vendor: "",
    online: f.speaks_modbus,
    name: defaultDeviceName(DRIVER.MODBUS_TCP, f.host),
  };
}

/** The unit a Modbus hit answered on: the identity's, else the gateway default of 1. An address, not a quantity. */
function modbusUnitOf(raw: string | undefined): number {
  const unit = Number(raw);
  return Number.isInteger(unit) && unit > 0 ? unit : 1;
}

/** A v2 discover hit -> the wizard's device row. A hit is a device that answered its driver's probe. */
function hitToDevice(h: DiscoverHit): DiscoveredDevice {
  const identity: Record<string, string> = {};
  for (const [k, v] of Object.entries(h.identity ?? {})) {
    if (v !== null && v !== undefined) identity[k] = String(v);
  }
  return {
    ip: h.ip,
    port: h.port,
    driver: h.driver,
    identity,
    // Another driver has no unit.
    unit_id: h.driver === DRIVER.MODBUS_TCP ? modbusUnitOf(identity.unit_id) : 0,
    vendor: identity.vendor ?? "",
    online: true,
    name: defaultDeviceName(h.driver, h.ip),
  };
}

/**
 * A discover answer -> device rows. `hits` is the v2 answer (every driver);
 * `found` is a pre-v2 gateway's own list, passed through by the server. The
 * server derives Modbus hits from `found`, so a found host already in `hits`
 * is skipped; what `found` adds is the hosts that had the port open but gave
 * no Modbus reply, which today's panel lists as "No reply". Hits come first,
 * in the gateway's order.
 */
export function discoverResultToDevices(res: DiscoverScanResult): DiscoveredDevice[] {
  const out = (res.hits ?? []).map(hitToDevice);
  const seen = new Set(out.map(deviceKey));
  for (const f of res.found ?? []) {
    const d = toDevice(f);
    const key = deviceKey(d);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(d);
  }
  return out;
}

/** A readable sniff cell -> a candidate register row. The agent reads one 16-bit
 *  register per address (int16), so multi-word wire types are composed later on
 *  the Map step, not here. */
function toRegister(c: SniffScanCell): SniffedRegister {
  const value = c.i16 != null ? c.i16 : c.bit == null ? 0 : c.bit ? 1 : 0;
  const prefix = SPACE_PREFIX[c.space] ?? "reg";
  return {
    address: c.address,
    data_type: "int16",
    reg_count: 1,
    value,
    guessed_name: `${prefix}_${c.address}`,
  };
}

/** Scans run on a real gateway through the platform API; with no backend there is none to ask. */
function requireLiveApi(): void {
  if (!isLiveApi) {
    throw new Error(
      "Scanning needs the live platform API. This build runs without a backend, so there is no gateway to scan from.",
    );
  }
}

// ── Client ──────────────────────────────────────────────────────────────────

export const gateway = {
  /**
   * Ask a gateway to sweep its plant network for devices. With no `cidr` the
   * gateway sweeps its own network (the one-click "Scan LAN" case). `drivers`
   * picks the protocols to probe, each on its default port (a v2 gateway);
   * omitted, only Modbus TCP is probed (TCP 502 or `port`, then a Modbus read),
   * which is all a pre-v2 gateway does. `ports` replaces a driver's default
   * ports (lib/iot/scan-ports builds it); sent only when non-empty.
   */
  discover: async (
    gatewayId: string,
    opts?: {
      cidr?: string;
      port?: number;
      unit_ids?: number[];
      drivers?: string[];
      ports?: Record<string, number[]>;
    },
  ): Promise<DiscoverResult> => {
    requireLiveApi();
    const cidr = opts?.cidr?.trim() || undefined;
    const res = await things.discover(gatewayId, {
      cidr,
      port: opts?.port,
      unit_ids: opts?.unit_ids,
      drivers: opts?.drivers && opts.drivers.length > 0 ? opts.drivers : undefined,
      ports: opts?.ports && Object.keys(opts.ports).length > 0 ? opts.ports : undefined,
    });
    const devices = discoverResultToDevices(res).map((d) => ({ ...d, subnet: cidr ?? "" }));
    return { devices, partial: res.partial === true };
  },

  /**
   * One page of a device's address space through the gateway (v2 gateways):
   * Modbus registers, OPC UA nodes, EtherNet/IP tags or an S7 / MELSEC block
   * scan page. `link` is the device's link params as entered, secrets inline
   * (used for this read, never stored).
   */
  browse: async (gatewayId: string, body: BrowseRequestBody): Promise<BrowseResult> => {
    requireLiveApi();
    const res = await things.browse(gatewayId, body);
    return { entries: Array.isArray(res?.entries) ? res.entries : [], next: res?.next ?? null };
  },

  /**
   * Read a device's registers through a gateway. Reads `count` holding
   * registers from `start` (default 0..63) in blocks of 16; each readable cell
   * becomes one candidate register the operator ticks and names.
   */
  sniff: async (
    gatewayId: string,
    ip: string,
    opts?: { unit_id?: number; port?: number; start?: number; count?: number },
  ): Promise<SniffResult> => {
    requireLiveApi();
    const res = await things.sniff(gatewayId, {
      host: ip.trim(),
      // Unit 0 (no unit answered the sweep) is left to the gateway's default of 1.
      unit_id: opts?.unit_id || undefined,
      port: opts?.port,
      holding: { start: opts?.start ?? SNIFF_START, count: opts?.count ?? SNIFF_COUNT },
      block_size: SNIFF_BLOCK,
    });
    return {
      ip: res.host,
      unit_id: res.unit_id,
      registers: (res.cells ?? []).filter((c) => c.ok).map(toRegister),
    };
  },
};

// ── Errors ──────────────────────────────────────────────────────────────────

export interface ScanErrorContext {
  /** Which relay call failed. */
  op: "discover" | "sniff" | "browse";
  /** Display name of the gateway the scan ran on. */
  gatewayName: string;
  /** The device a sniff targeted. */
  deviceIp?: string;
}

/**
 * Operator-facing text for a failed discover or sniff. The API drops the detail
 * of every 5xx, so the gateway-offline case is written here from the status and
 * error code. A 4xx carries the server's own sentence (a rejected range, "a scan
 * is already running on this gateway"), which wins over anything guessed here.
 */
export function scanErrorMessage(err: unknown, ctx: ScanErrorContext): string {
  const gw = ctx.gatewayName || "The gateway";
  const status = statusOf(err);
  if (status === undefined) {
    // No HTTP answer at all (the fetch itself failed), or the stub-mode guard above.
    if (err instanceof TypeError) return "Could not reach the server. Check your connection and try again.";
    return err instanceof Error && err.message ? err.message : "The scan failed.";
  }
  const code = errorCodeOf(err);
  const detail = errorDetailOf(err);
  if (status === 401) return "Your session has expired. Sign in again, then retry.";
  if (status === 403) {
    return "You do not have permission to scan from gateways. Ask an organisation admin for gateway commissioning access.";
  }
  if (code === "gateway_not_found") {
    return `${gw} was not found in this organisation. Refresh the page and pick another gateway.`;
  }
  if (status === 404) return "This server does not offer cloud scans yet. It needs a platform update.";
  if (code === "gateway_not_claimed") {
    return `${gw} is not commissioned yet, so it cannot run scans. Claim it with a claim code from the Gateways page, then try again.`;
  }
  if (status === 504) {
    return `${gw} did not answer. Check that the gateway PC is running and connected, then try again.`;
  }
  if (code === "command_publish_failed") {
    return "The platform could not send the scan to the gateway just now. Try again in a moment.";
  }
  if (code === "scan_unavailable") {
    return "The platform is still connecting to the device network. Try again in a few seconds.";
  }
  if (code === "scan_failed") {
    // 424: the gateway ran the scan but the device did not answer. The gateway's own
    // reason follows ": " as it is, for DeviceErrorText (lib/iot/device-errors.ts) to
    // explain per protocol, with the raw text under Details.
    if (detail?.trim()) {
      return ctx.op === "discover"
        ? `${gw} could not finish the scan: ${detail.trim()}`
        : `The device at ${ctx.deviceIp ?? "that address"} did not answer: ${detail.trim()}`;
    }
    if (ctx.op === "browse") {
      return `The device at ${ctx.deviceIp ?? "that address"} did not answer. Check its connection settings, and that ${gw} can reach it.`;
    }
    return ctx.op === "sniff"
      ? `The device at ${ctx.deviceIp ?? "that address"} did not answer. Check its IP address, unit id and port, and that ${gw} can reach it.`
      : `${gw} could not finish the scan. Try again, or scan a narrower range.`;
  }
  if (code === "browse_unsupported") {
    return "This device's protocol has no address listing. Enter the point addresses by hand.";
  }
  if (code === "gateway_cannot_browse") {
    return `${gw} cannot list this device's points: its firmware has not announced this protocol with multi-protocol config (GatewayPro v0.3 or later). Enter the addresses by hand, or update the gateway.`;
  }
  if (code === "invalid_link") {
    return detail
      ? `The connection settings are not valid: ${asSentence(detail)}`
      : "The connection settings are not valid. Check them and try again.";
  }
  if (code === "invalid_body" && /unknown field "ports"/.test(detail ?? "")) {
    return "This server cannot scan on custom ports yet: it needs a platform update. Put the ports back to their defaults, or add the device by hand with its port.";
  }
  if (code === "invalid_drivers") {
    return detail ? asSentence(detail) : `${gw} cannot scan for one of the chosen protocols.`;
  }
  if (code === "command_publisher_unavailable") {
    return "This server cannot send commands to gateways yet, so scans cannot run. Ask your platform admin to enable the gateway command channel.";
  }
  if (detail) {
    if (/could not detect the local subnet/i.test(detail)) {
      return `${gw} could not work out its own network. Enter a scan range, for example 192.168.1.0/24, and scan again.`;
    }
    return asSentence(detail);
  }
  const what =
    ctx.op === "sniff" ? "Reading the registers" : ctx.op === "browse" ? "Reading the device's points" : "The scan";
  return `${what} failed (HTTP ${status}).`;
}

/** A Go-style error string ("scan range ... is too wide") as a display sentence. */
function asSentence(s: string): string {
  const t = s.trim();
  const capped = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?]$/.test(capped) ? capped : `${capped}.`;
}
